import type { SandboxFile } from "@agent-ui/features/chat/hooks/useReploAgentConnection";
import type {
  PageTab,
  PageTabContentType,
} from "@agent-ui/features/page-renderer/store/pageTabsStore";

import * as React from "react";

import { useSessionActions } from "@agent-ui/features/chat/hooks/useOpencodeSession";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import AddTabModal from "@agent-ui/features/page-renderer/components/navbar/AddTabModal";
import {
  getActiveTabId,
  getOpenTabsSnapshot,
  useOpenTabs,
  usePageTabsActions,
  useRecentlyClosedTabs,
} from "@agent-ui/features/page-renderer/store/pageTabsStore";
import { useSandboxNavigate } from "@agent-ui/store/sandboxNavigationStore";
import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuGroup,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  BarChart3,
  FileText,
  ListCollapse,
  Mail,
  Megaphone,
  RotateCcw,
  X,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

const TAB_TYPE_ICONS: Record<PageTabContentType, React.ElementType> = {
  page: FileText,
  ad: Megaphone,
  email: Mail,
  report: BarChart3,
};

function TabTypeIcon({
  type,
  size = 14,
  className = "text-muted",
}: {
  type: PageTabContentType;
  size?: number;
  className?: string;
}) {
  const Icon = TAB_TYPE_ICONS[type];

  return <Icon size={size} className={className} />;
}

function PageTabItem({
  tab,
  isActive,
  onActivate,
  onClose,
  isClosable,
}: {
  tab: PageTab;
  isActive: boolean;
  onActivate: () => void;
  onClose: () => void;
  isClosable: boolean;
}) {
  const labelRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = labelRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const label = tab.content.pageRoute;

  const button = (
    <button
      type="button"
      onClick={onActivate}
      className={twMerge(
        "group/tab relative flex items-center gap-1.5 h-11 px-3 text-sm font-medium select-none cursor-pointer rounded-t-xl border-0.5 border-b-0 transition-colors duration-150 outline-none focus:outline-none focus-visible:outline-none max-w-[200px] min-w-[100px] shrink-0",
        isActive
          ? "bg-white text-default border-border z-10"
          : "bg-transparent text-muted border-transparent hover:bg-white/60 hover:text-default",
      )}
    >
      <span className="relative shrink-0">
        <TabTypeIcon type={tab.content.type} />
        {tab.content.status && (
          <span
            className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full border border-white bg-primary"
            aria-label="Page updated"
          />
        )}
      </span>
      <span
        ref={labelRef}
        className="flex-1 text-left whitespace-nowrap truncate"
      >
        {label}
      </span>
      {isClosable && (
        <Button
          icon
          variant="tertiary"
          size="sm"
          UNSAFE_className={twMerge(
            "size-4 rounded-sm transition-opacity duration-100",
            isActive && "opacity-100",
            !isActive && "opacity-0 group-hover/tab:opacity-100",
          )}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={`Close ${label}`}
        >
          <X size={10} />
        </Button>
      )}

      {/* Inverse rounded corners at the base of the active tab, with border */}
      {isActive && (
        <>
          <span
            className="absolute bottom-0 -left-2 size-2 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 0 0, transparent 7.5px, var(--color-border) 7.5px, var(--color-border) 8px, white 8px)",
            }}
          />
          <span
            className="absolute bottom-0 -right-2 size-2 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 100% 0, transparent 7.5px, var(--color-border) 7.5px, var(--color-border) 8px, white 8px)",
            }}
          />
        </>
      )}
    </button>
  );

  if (!isTruncated) {
    return button;
  }

  return (
    <Tooltip content={label} triggerAsChild>
      {button}
    </Tooltip>
  );
}

// ─── Helpers to navigate to a tab ────────────────────────────────────────────

function useTabNavigation() {
  const sandboxNavigate = useSandboxNavigate();

  const navigateToTab = React.useCallback(
    (tab: PageTab) => {
      if (tab.content.pageRoute) {
        sandboxNavigate(tab.content.pageRoute);
      }
    },
    [sandboxNavigate],
  );

  return { navigateToTab };
}

function TabListMenu({
  onActivate,
  onClose,
  onReopen,
  activeTabId,
}: {
  onActivate: (tab: PageTab) => void;
  onClose: (tabId: string) => void;
  onReopen: (tabId: string) => void;
  activeTabId: string | null;
}) {
  const openTabs = useOpenTabs();
  const recentlyClosedTabs = useRecentlyClosedTabs();

  return (
    <Menu>
      <MenuTrigger>
        <Button
          icon
          variant="tertiary"
          size="lg"
          aria-label="All tabs"
          UNSAFE_className="rounded-t-xl rounded-b-none hover:bg-white/60 focus-visible:outline-none focus-visible:ring-0"
        >
          <ListCollapse size={14} />
        </Button>
      </MenuTrigger>
      <MenuContent align="end" sideOffset={8} UNSAFE_className="w-[260px]">
        <MenuGroup>
          <MenuLabel>Tabs in this window</MenuLabel>
          {openTabs.map((tab) => (
            <MenuItem
              key={tab.id}
              label={tab.content.pageRoute}
              selected={tab.id === activeTabId}
              startEnhancer={
                <TabTypeIcon type={tab.content.type} size={12} className="" />
              }
              endEnhancer={
                openTabs.length > 1 ? (
                  <span
                    role="button"
                    className="opacity-0 group-hover/dropdown-menu-item:opacity-100 text-muted hover:text-default transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose(tab.id);
                    }}
                  >
                    <X size={12} />
                  </span>
                ) : undefined
              }
              onClick={() => onActivate(tab)}
            />
          ))}
        </MenuGroup>
        {recentlyClosedTabs.length > 0 && (
          <>
            <MenuSeparator />
            <MenuGroup>
              <MenuLabel>Recently closed</MenuLabel>
              {recentlyClosedTabs.map((tab) => (
                <MenuItem
                  key={`closed-${tab.id}`}
                  label={tab.content.pageRoute}
                  startEnhancer={
                    <TabTypeIcon
                      type={tab.content.type}
                      size={12}
                      className=""
                    />
                  }
                  endEnhancer={<RotateCcw size={12} className="text-muted" />}
                  onClick={() => onReopen(tab.id)}
                />
              ))}
            </MenuGroup>
          </>
        )}
      </MenuContent>
    </Menu>
  );
}

function PageTabBar() {
  const openTabs = useOpenTabs();
  const [searchParams] = useSearchParams();
  const pageRoute = searchParams.get("pageRoute");
  const activeTabId = getActiveTabId({ openTabs, pageRoute });

  const { closeTab, openTab, clearTabStatus, reopenTab } = usePageTabsActions();
  const { navigateToTab } = useTabNavigation();
  const { setFiles } = useCurrentChatContext();
  const { sendPrompt } = useSessionActions();

  const handleActivate = React.useCallback(
    (tab: PageTab) => {
      navigateToTab(tab);
      clearTabStatus(tab.id);
    },
    [navigateToTab, clearTabStatus],
  );

  const handleClose = React.useCallback(
    (tabId: string) => {
      const tabs = openTabs;
      const idx = tabs.findIndex((t) => t.id === tabId);
      const isActive = tabId === activeTabId;

      closeTab(tabId);

      if (isActive) {
        const remaining = tabs.filter((t) => t.id !== tabId);
        if (remaining.length > 0) {
          const nextIdx = idx >= remaining.length ? remaining.length - 1 : idx;
          const nextTab = remaining[nextIdx];
          if (nextTab) {
            navigateToTab(nextTab);
          }
        }
      }
    },
    [closeTab, navigateToTab, openTabs, activeTabId],
  );

  const handleReopen = React.useCallback(
    (tabId: string) => {
      reopenTab(tabId);
      const tab = getOpenTabsSnapshot().find((t) => t.id === tabId);
      if (tab) {
        navigateToTab(tab);
      }
    },
    [reopenTab, navigateToTab],
  );

  const handlePageSelect = React.useCallback(
    (file: SandboxFile) => {
      const tab: PageTab = {
        id: file.route,
        content: {
          type: "page",
          pageRoute: file.route,
          status: null,
        },
      };
      openTab(tab);
      navigateToTab(tab);
    },
    [openTab, navigateToTab],
  );

  const handlePromptSubmit = React.useCallback(
    (prompt: string) => {
      setFiles([]);
      // TODO (Max, 2026-03-10, REPL-24979): Add files to the prompt once I remove files stuff from CurrentChatContext
      void sendPrompt({ prompt, files: [] });
    },
    [sendPrompt, setFiles],
  );

  if (openTabs.length === 0) {
    return null;
  }

  return (
    <div className="flex items-end w-full bg-surface shadow-[inset_0_-0.5px_0_0_var(--color-border)] pl-1 pr-3 pt-3">
      <div className="relative flex-1 min-w-0">
        <div className="flex items-end overflow-x-auto no-scrollbar pl-2 pr-6">
          {openTabs.map((tab) => (
            <PageTabItem
              key={tab.id}
              tab={tab}
              isActive={tab.id === activeTabId}
              onActivate={() => handleActivate(tab)}
              onClose={() => handleClose(tab.id)}
              isClosable={openTabs.length > 1}
            />
          ))}
          <AddTabModal
            onPageSelect={handlePageSelect}
            onPromptSubmit={handlePromptSubmit}
          />
        </div>
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-surface to-transparent" />
      </div>

      <TabListMenu
        onActivate={handleActivate}
        onClose={handleClose}
        onReopen={handleReopen}
        activeTabId={activeTabId}
      />
    </div>
  );
}

export default PageTabBar;
